import { FieldWrapper } from "components/ui/FieldWrapper";
import { Select, type SelectProps } from "components/ui/Select";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

export type RhfSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control?: Control<T>;
  label?: string;
  required?: boolean;
  hint?: string;
  onValueChange?: (value: string) => void;
} & Omit<SelectProps, "name" | "value" | "onChange" | "onBlur">;

/**
 * React Hook Form アダプタ for Select。
 * @param {RhfSelectProps<T>} props RhfSelect の props
 * @return {JSX.Element} FieldWrapper で包まれた Select
 */
export function RhfSelect<T extends FieldValues>({
  name,
  control,
  label,
  required,
  hint,
  onValueChange,
  ...selectProps
}: RhfSelectProps<T>): JSX.Element {
  const { field, fieldState } = useController<T>({ name, control });

  return (
    <FieldWrapper
      label={label}
      required={required}
      hint={hint}
      error={fieldState.error?.message}
    >
      <Select
        {...selectProps}
        name={field.name}
        value={field.value != null ? String(field.value) : undefined}
        onChange={newValue => {
          field.onChange(newValue);
          if (newValue) {
            onValueChange?.(newValue);
          }
        }}
        onBlur={field.onBlur}
      />
    </FieldWrapper>
  );
}
